/**
 * Solana transaction fee estimation via Solana JSON-RPC.
 *
 * @remarks
 * Uses `getFeeForMessage` to obtain the base transaction fee and
 * `getRecentPrioritizationFees` to build slow/medium/fast priority fee tiers.
 * Fee values are expressed in lamports (base) and microLamports/CU (priority).
 *
 * @internal
 */
import { txDecode } from "@blockdaemon/solana-staking-sdk";
import { normalizeChainIdString } from "../../../shared/caip2";
import { validateSolanaFeeRequest } from "../validate";
/** Fixed base fee per signature slot in lamports (Solana protocol constant). */
const SOLANA_BASE_FEE_PER_SIGNATURE = 5000;
/**
 * Default compute unit budget used to convert microLamports/CU priority fee rates
 * into total lamport amounts. Chosen as a conservative upper bound for a staking
 * transaction with 3-4 instructions.
 */
const DEFAULT_CU_BUDGET = 200_000;
/**
 * Estimates transaction fees for a Solana transaction using JSON-RPC.
 *
 * @remarks
 * Calls `getFeeForMessage` for the base fee and `getRecentPrioritizationFees`
 * for priority fee tiers. Both calls fall back gracefully on failure.
 *
 * The `IEstimateFeesResponse` fields are interpreted as follows for Solana:
 * - `gasEstimate`: number of required signature slots in the transaction
 * - `baseFeePerGas`: base fee per signature slot in lamports (typically 5000)
 * - `maxPriorityFeePerGas`: priority fee rate in microLamports per Compute Unit
 * - `maxFeePerGas`: baseFeePerSig + (priorityRate * DEFAULT_CU_BUDGET / 1_000_000) per sig
 * - `estimatedFee`: total lamports for the full transaction (`baseFee` + tier priority lamports),
 *   same idea as the legacy `estimatedCost.{slow|medium|fast}` field
 *
 * @param request - Fee estimation request; `transaction.data` must be a base64 serialized Solana tx
 * @param chainService - Chain service for Solana RPC access and address validation
 * @param logger - Logger instance
 * @param retry - Retry service for transient RPC failures
 * @returns Promise resolving to IEstimateFeesResponse populated with Solana fee data
 */
export async function _estimateSolanaFee(request, chainService, logger, retry) {
    validateSolanaFeeRequest(request, chainService);
    const chainId = request.chainId;
    const normalizedChainId = normalizeChainIdString(chainId);
    logger.debug(`estimateSolanaFee: Starting fee estimation for chain ${chainId}`);
    const base64Tx = request.transaction.data;
    const { signatureCount, messageBase64 } = extractMessageFromTransaction(base64Tx);
    logger.debug(`estimateSolanaFee: Parsed transaction — ${signatureCount} signature slot(s)`);
    const rpcBaseFee = await _getSolanaBaseFee(chainService, chainId, messageBase64, logger, retry);
    // Ensure the base fee covers all signature slots. When the RPC falls back to the
    // per-signature constant (5000), this scales it up to the full transaction amount.
    const baseFee = Math.max(rpcBaseFee, signatureCount * SOLANA_BASE_FEE_PER_SIGNATURE);
    const baseFeePerSignature = signatureCount > 0 ? Math.ceil(baseFee / signatureCount) : SOLANA_BASE_FEE_PER_SIGNATURE;
    const priorityFees = await _getSolanaPriorityFees(chainService, chainId, logger, retry);
    const buildTier = (priorityRateMicroLamports) => {
        const priorityLamports = Math.floor((priorityRateMicroLamports * DEFAULT_CU_BUDGET) / 1_000_000);
        const priorityPerSignature = Math.floor(priorityLamports / Math.max(signatureCount, 1));
        const maxFeePerSignature = baseFeePerSignature + priorityPerSignature;
        logger.debug("estimateSolanaFee: Fee calculation", {
            baseFee,
            baseFeePerSignature,
            priorityFees,
            priorityLamports,
            priorityPerSignature,
            maxFeePerSignature,
        });
        const estimatedTotalLamports = baseFee + priorityLamports;
        return {
            baseFeePerGas: baseFeePerSignature.toString(),
            maxPriorityFeePerGas: priorityRateMicroLamports.toString(),
            maxFeePerGas: maxFeePerSignature.toString(),
            estimatedFee: estimatedTotalLamports.toString(),
        };
    };
    const result = {
        chainId: normalizedChainId,
        timestamp: Date.now(),
        gasEstimate: signatureCount.toString(),
        slow: buildTier(priorityFees.slow),
        medium: buildTier(priorityFees.medium),
        fast: buildTier(priorityFees.fast),
    };
    logger.debug("estimateSolanaFee: Completed", { baseFee, priorityFees });
    return result;
}
/**
 * Extracts or passes through the Solana Message for use with `getFeeForMessage`.
 *
 * @remarks
 * Handles two formats that may appear in `IEstimateFeesRequest.transaction.data`:
 *
 * **Full transaction format** (produced by e.g. `solana-staking-sdk` `.serialize()`):
 * Deserialized via `txDecode` from `@blockdaemon/solana-staking-sdk`, which returns
 * a `Transaction` with `.signatures` and `.serializeMessage()`.
 *
 * **Raw message format** (produced by Blockdaemon stake integration API):
 * The bytes are the message directly with no signature-slot prefix.
 * In this case the data is passed through unchanged; the signature count is
 * read from the first byte of the message header (`numRequiredSignatures`).
 *
 * @param base64Data - Base64-encoded transaction or message data from `buildStakeTx`
 * @returns Signature count and base64-encoded message ready for `getFeeForMessage`
 * @throws {Error} If the data is fewer than 3 bytes (minimum valid message size)
 */
export function extractMessageFromTransaction(base64Data) {
    const bytes = Buffer.from(base64Data, "base64");
    if (bytes.length < 3) {
        throw new Error(`Invalid Solana transaction data: too short (${bytes.length} bytes, minimum 3)`);
    }
    try {
        const tx = txDecode(base64Data, "base64");
        const messageBytes = tx.serializeMessage();
        return {
            signatureCount: tx.signatures.length,
            messageBase64: Buffer.from(messageBytes).toString("base64"),
        };
    }
    catch {
        // Raw message format: the data IS the message; read sigCount from message header
        const numRequiredSignatures = bytes[0] ?? 1;
        return {
            signatureCount: numRequiredSignatures,
            messageBase64: base64Data,
        };
    }
}
/**
 * Fetches the base transaction fee in lamports using `getFeeForMessage`.
 *
 * @remarks
 * Falls back to `signatureCount * SOLANA_BASE_FEE_PER_SIGNATURE` if the RPC call
 * fails or returns null (e.g. expired blockhash in an unsigned transaction).
 *
 * @param chainService - Chain service providing Solana RPC access
 * @param chainId - Solana chain identifier
 * @param messageBase64 - Base64-encoded serialized Solana Message
 * @param logger - Logger instance
 * @param retry - Retry service
 * @returns Base transaction fee in lamports
 */
export async function _getSolanaBaseFee(chainService, chainId, messageBase64, logger, retry) {
    try {
        const result = await retry.rpc(() => chainService.callSolanaRpc(chainId, "getFeeForMessage", [
            messageBase64,
            { commitment: "confirmed" },
        ]));
        const feeResult = result;
        if (feeResult.value !== null && feeResult.value !== undefined) {
            logger.debug(`estimateSolanaFee: Base fee from RPC: ${feeResult.value} lamports`);
            return feeResult.value;
        }
        logger.warn("estimateSolanaFee: getFeeForMessage returned null (blockhash may be expired in unsigned tx), using fixed fee");
        return SOLANA_BASE_FEE_PER_SIGNATURE;
    }
    catch (error) {
        logger.warn("estimateSolanaFee: Failed to get base fee from RPC, using fixed fee:", error);
        return SOLANA_BASE_FEE_PER_SIGNATURE;
    }
}
/**
 * Fetches recent prioritization fees and computes slow/medium/fast percentile tiers.
 *
 * @remarks
 * Uses the 25th, 50th, and 75th percentile of recent per-slot prioritization fees.
 * Values are in microLamports per Compute Unit. Returns zeros if no data is available.
 *
 * @param chainService - Chain service providing Solana RPC access
 * @param chainId - Solana chain identifier
 * @param logger - Logger instance
 * @param retry - Retry service
 * @returns Priority fee tiers in microLamports per Compute Unit
 */
export async function _getSolanaPriorityFees(chainService, chainId, logger, retry) {
    const defaultFees = { slow: 0, medium: 0, fast: 0 };
    try {
        const result = await retry.rpc(() => chainService.callSolanaRpc(chainId, "getRecentPrioritizationFees", []));
        const fees = result;
        if (!Array.isArray(fees) || fees.length === 0) {
            logger.debug("estimateSolanaFee: No recent prioritization fees available, defaulting to 0");
            return defaultFees;
        }
        const sorted = fees
            .map((f) => f.prioritizationFee)
            .filter((f) => typeof f === "number")
            .sort((a, b) => a - b);
        if (sorted.length === 0) {
            return defaultFees;
        }
        const len = sorted.length;
        const slow = sorted[Math.floor(len * 0.25)] ?? 0;
        const medium = sorted[Math.floor(len * 0.5)] ?? 0;
        const fast = sorted[Math.floor(len * 0.75)] ?? 0;
        logger.debug("estimateSolanaFee: Priority fees (microLamports/CU):", {
            slow,
            medium,
            fast,
        });
        return { slow, medium, fast };
    }
    catch (error) {
        logger.warn("estimateSolanaFee: Failed to get priority fees, defaulting to 0:", error);
        return defaultFees;
    }
}
//# sourceMappingURL=data:application/json;base64,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